import * as React from "react";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { fileUploadStore } from "./FileUploadStore";
import { Row, Table as AntdTable } from "antd";
import { dayjs } from "Config/dayjs";
import { FormatData } from "utils/FormatData";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { Button } from "Components/General/Button";
import { collectionFolderStore } from "../CollectionFolder/CollectionFolderStore";
import { CollectionFolderFile } from "../CollectionFolder/interfaces";
import { EllipsisLeft } from "Components/General/EllipsisLeft";
import { projectsDataStore } from "Stores";
import { RouterStore } from "Config/CustomBrowserRouter";
import { DetailListActions, DropdownActionConfig } from "Components/General/DetailListActions";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { LabelCalculator } from "../Calculator/LabelCalculator";
import { labelCalculatorStore } from "../Calculator/LabelCalculatorStore";
import { Table } from "Components/General/Table";

const buttonStyles: React.CSSProperties = { marginRight: 5 };

interface UploadCompleteProps extends WithTranslation, WithModal {
  onDone: () => Promise<void>;
}

export const UploadComplete = withModal(
  withTranslation([I18N_NS.PROJECT, I18N_NS.COMPONENT_UPLOAD])(
    observer(
      class extends React.Component<
        UploadCompleteProps,
        { selectedRowKeys: number[]; files: API_UploadFile[]; doneLoading: boolean }
      > {
        constructor(params: UploadCompleteProps) {
          super(params);
          this.state = {
            selectedRowKeys: [],
            files: [],
            doneLoading: false,
          };
        }

        componentDidMount() {
          this.setState({ files: fileUploadStore.result?.files?.slice() || [] });
        }

        addToCollectionFolder = () => {
          collectionFolderStore.addFiles(
            fileUploadStore.result.files.map((file) => ({ ...file, tag: "UPLOADED" } as CollectionFolderFile))
          );
          /**
           * The method below is called to update data for newly downloaded files
           * (since the downloaded files object do not include the {properties} and {fileActions} properties)
           * which are needed to process user rights for this file
           * Need to improve this implementation.
           */
          collectionFolderStore.init();
          this.props.onDone();
          collectionFolderStore.toggleModal();
        };

        handleDone = async () => {
          try {
            this.setState({ doneLoading: true });
            await this.props.onDone();
          } catch (error) {
            throw error;
          } finally {
            this.setState({ doneLoading: false });
          }
        };

        goToWorkflow = () => {
          const worklistEntryId = fileUploadStore.result.actionInfoRest?.worklistEntryId;
          if (worklistEntryId) {
            const workflowLink = `/project/${projectsDataStore.projectId}/workflows/assignments/${worklistEntryId}`;
            fileUploadStore.reset();
            RouterStore.push(workflowLink);
          } else {
            throw new Error("worklistEntryId not found in response object");
          }
        };

        get uploadedFilesActions(): DropdownActionConfig[] {
          const { t, showModal } = this.props;
          const { selectedRowKeys, files } = this.state;
          const selectedFiles = selectedRowKeys.map((key) => files.find((item) => item.id === +key)).filter(Boolean);
          const isEmptySelection = !selectedFiles.length;

          const actions = [];

          if (projectsDataStore.functions?.includes("FILE_LABELS")) {
            actions.push({
              label: `${t("Labels")}...`,
              onClick: () => {
                labelCalculatorStore.setMode("FILE");
                showModal(LabelCalculator, {
                  modalProps: {
                    title: " ",
                    width: "100%",
                    style: { maxWidth: 1200 },
                    footer: null,
                  },
                  componentProps: { files: selectedFiles },
                });
              },
              disabled: isEmptySelection,
            });
          }

          return actions;
        }

        render() {
          const { t } = this.props;
          const { files, doneLoading } = this.state;
          const isGoToWorkflow = fileUploadStore.result.actionInfoRest?.status === "IN_PROGRESS";
          const isRegularUploadMode = fileUploadStore.module === "DOCUMENTS";
          const rowSelection = {
            selectedRowKeys: this.state.selectedRowKeys,
            onChange: (selectedRowKeys: number[]) => {
              this.setState({ selectedRowKeys });
            },
          };

          return (
            <div className="flex-space-between" style={{ height: "100%" }}>
              <div>
                <Row className="mb-2" align="middle">
                  {isRegularUploadMode && (
                    <DetailListActions primaryActions={[]} dropdownActions={this.uploadedFilesActions} />
                  )}
                  {t("Files were successfully uploaded")}:
                </Row>
                <Table<API_UploadFile>
                  name="uploadComplete"
                  dataSource={files}
                  rowKey={"id"}
                  pagination={files.length < 11 ? false : undefined}
                  rowSelection={isRegularUploadMode ? rowSelection : null}
                >
                  <AntdTable.Column<API_UploadFile> dataIndex="name" title={t("Filename")} />
                  <AntdTable.Column<API_UploadFile> dataIndex="index" title={t("Index")} />
                  {isRegularUploadMode && (
                    <AntdTable.Column<API_UploadFile>
                      dataIndex="dirname"
                      title={t("File path")}
                      render={(text) => {
                        const path = text.startsWith("/") ? text : "/" + text;
                        return <EllipsisLeft text={path} />;
                      }}
                      width={100}
                    />
                  )}
                  <AntdTable.Column<API_UploadFile>
                    dataIndex="created"
                    title={t("Last change")}
                    defaultSortOrder="descend"
                    render={(text, record) => dayjs(+record.created).format("lll")}
                  />
                  <AntdTable.Column<API_UploadFile>
                    dataIndex="size"
                    title={t("Size")}
                    render={(text, record) => FormatData.formatBytes(record.size, 1)}
                  />
                </Table>
              </div>
              {isGoToWorkflow ? (
                <ButtonsRow justify="end" className="mb-2">
                  <Button type="primary" onClick={this.goToWorkflow}>
                    {t(`Done - process workflow`)}
                  </Button>
                </ButtonsRow>
              ) : (
                <ButtonsRow justify="end" className="mb-2">
                  <Button type="primary" style={buttonStyles} onClick={this.handleDone} loading={doneLoading}>
                    {t(`componentUpload:::Done`)}
                  </Button>
                  {isRegularUploadMode && (
                    <Button onClick={this.addToCollectionFolder}>{t("Add files to collection folder")}</Button>
                  )}
                </ButtonsRow>
              )}
            </div>
          );
        }
      }
    )
  )
);
